<template>
  <div class="progress-bar">
    <div class="progress-bar-labels">
      <div class="right-side">
        <div class="label" :class="{ 'grayed-out': grayedOut }">{{ rightLabel }}</div>
        <div class="number colored bold" :class="{'grayed-out': grayedOut}">
          <div v-if="isExceedingAmount" class="icon">
            <img :src="exclamationMarkPath">
          </div>
          <span v-if="placeholder">{{ placeholder }}</span>
          <decimal-number v-else :value="rightAmount" /> ₪
        </div>
      </div>

      <div v-if=!hideLeftLabel class="left-side" :class="{ 'grayed-out': grayedOut }">
        <div class="label">{{ leftLabel }}</div>
        <div class="number" :class="{ 'grayed-out': grayedOut }">
          <span v-if="placeholder">{{ placeholder }}</span>
          <animated-decimal-number v-else :value="totalAmount" /> ₪
        </div>
      </div>
    </div>

    <animated-bar :totalAmount="totalAmount"
                  :up-to-now-amount="rightAmount"
                  :animate-in-viewport="animateInViewport"
                  :bar-color="currentColor"/>
    <div v-if="isExceedingAmount" class="exceeding-warning" :class="{ 'grayed-out': grayedOut }">
      חריגה של {{ formatAmount(Math.abs(amountsDiff)) }}
    </div>
  </div>
</template>

<script>
import progressBarColorStyles from '../utils/progress-bar-color-styles';
import { formatAmountWithFraction } from '../utils/money-utils';
import AnimatedBar from './AnimatedBar.vue';
import AnimatedDecimalNumber from './AnimatedDecimalNumber.vue';
import DecimalNumber from './DecimalNumber.vue';
import redMark from '../assets/icons/exclamation-mark/exclamation-mark-red.svg';
import grayMark from '../assets/icons/exclamation-mark/exclamation-mark-gray.svg';

export default {
  name: 'ProgressBar',
  components: {
    AnimatedDecimalNumber,
    AnimatedBar,
    DecimalNumber,
  },
  props: {
    rightLabel: {
      type: String,
      required: true,
    },
    rightAmount: {
      type: Number,
      required: false,
    },
    leftLabel: {
      type: String,
      required: true,
    },
    leftAmount: {
      type: Number,
      required: false,
    },
    showWarningIfExceedingAmount: {
      type: Boolean,
      required: false,
    },
    hideLeftLabel: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      required: false,
      default: 'blue',
      validate: color => ['grey', 'blue', 'yellow', 'pink', 'green', 'orange'].includes(color),
    },
    grayedOut: {
      type: Boolean,
      required: false,
    },
    placeholder: {
      type: String,
      required: false,
    },
    animateInViewport: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      colorVars: progressBarColorStyles,
    };
  },
  computed: {
    isExceedingAmount() {
      return this.showWarningIfExceedingAmount && this.rightAmount > this.leftAmount;
    },
    amountsDiff() {
      return this.leftAmount - this.rightAmount;
    },
    totalAmount() {
      return this.leftAmount || 0;
    },
    exclamationMarkPath() {
      // This is only used when is isExceedingAmount is true, so we need to handle only
      // grayed out or red mark. No need to have all the colors.
      if (this.grayedOut) {
        return grayMark;
      }
      return redMark;
    },
    currentColor() {
      if (this.grayedOut) {
        return 'grey';
      }
      if (this.isExceedingAmount) {
        return 'yellow';
      }
      return this.color;
    },
  },
  methods: {
    formatAmount(amount) {
      const formattedAmount = formatAmountWithFraction(amount);
      return `${formattedAmount} ₪`;
    },
  },
};
</script>

<style lang="scss">
  @import '../scss/riseup-colors';

  .progress-bar {
    margin-top: 15px;

    .progress-bar-labels {
      display: flex;
      justify-content: space-between;
      margin-bottom: 5px;
      color: $riseup_black;
      font-size: 10px;

      .left-side {
        text-align: left;

        &.grayed-out {
          color: $riseup_gray_2;
        }
      }

      .label {
        margin-bottom: 5px;
        font-size: 12px;
        line-height: 11px;

        &.grayed-out {
          color: $riseup_gray_2;
        }
      }

      .number {
        font-size: 18px;
        line-height: 21px;
        &.colored {
          color: v-bind('colorVars[currentColor].number');
        }

        &.bold {
          font-weight: bold;
        }

        &.grayed-out {
          color: $riseup_gray_2;
        }

        .icon {
          display: inline-block;
          margin-left: 2px;
          vertical-align: middle;
          margin-bottom: 5px;
          height: 13px;
        }
      }
    }

    .danger-bar {
      height: 15px;
    }

    .exceeding-warning {
      margin-top: 14px;
      text-align: left;
      font-size: 12px;
      font-weight: bold;
      line-height: 10px;

      &.danger {
        color: $riseup_danger_red;
      }

      &.grayed-out {
        color: $riseup_gray_2;
      }
    }
  }
</style>
